/**
 * Central configuration for Enrichly parameters.
 *
 * Parameters are split into two categories:
 * 1. URL_PARAMS - Passed in iframe URL, applied immediately (visual/functional)
 * 2. CONTEXT_PARAMS - Passed via postMessage, can wait (tracking/sensitive)
 */

import { SupportedOrgs } from './storeConfig';

export interface UrlParam {
  /** URL query parameter key */
  urlKey: string;
  /** Key in window.enrichlyContext */
  contextKey: string;
  /** Whether this param affects theming (triggers CSS variable update) */
  isThemeParam?: boolean;
  /** Validation function */
  validate?: (value: string) => boolean;
}

export interface ContextParam {
  /** Key in window.enrichlyContext */
  contextKey: string;
  /** Shopify cart attribute label - if set, value is sent to cart */
  cartAttribute?: string;
  /** URL fallback key - used in standalone mode */
  urlFallbackKey?: string;
  /** Validation function */
  validate?: (value: string) => boolean;
}

/**
 * URL parameters - passed in iframe URL, applied immediately on page load.
 * Used for visual params that need to be set before render.
 */
export const URL_PARAMS: UrlParam[] = [
  {
    urlKey: 'primaryColor',
    contextKey: 'primaryColor',
    isThemeParam: true,
    validate: v => /^[0-9A-Fa-f]{6}$/.test(v),
  },
  {
    urlKey: 'textColorOverride',
    contextKey: 'textColorOverride',
    isThemeParam: true,
    validate: v => /^[0-9A-Fa-f]{6}$/.test(v),
  },
  {
    urlKey: 'perPage',
    contextKey: 'perPage',
    validate: v => /^\d{1,3}$/.test(v) && +v > 0 && +v <= 50,
  },
  {
    urlKey: 'navbar',
    contextKey: 'navbar',
  },
];

/**
 * Context parameters - passed via postMessage from parent window.
 * Used for tracking/sensitive params that don't need to be in URL.
 * In standalone mode, falls back to reading from URL.
 */
export const CONTEXT_PARAMS: ContextParam[] = [
  {
    contextKey: 'orgId',
    urlFallbackKey: 'orgId',
    validate: v => (Object.values(SupportedOrgs) as string[]).includes(v),
  },
  {
    contextKey: 'collectionHandle',
    urlFallbackKey: 'collectionHandle',
  },
  {
    contextKey: 'contactId',
    cartAttribute: 'Contact ID',
    urlFallbackKey: 'contactId',
  },
  {
    contextKey: 'salesforceId',
    cartAttribute: 'Salesforce ID',
    urlFallbackKey: 'sfid',
  },
];

/** Get params that should be sent as cart attributes */
export function getCartParams(): ContextParam[] {
  return CONTEXT_PARAMS.filter(p => p.cartAttribute);
}
